import { useState, useEffect } from 'react';
import { paymentService } from '../services/paymentService';
import { useAuth } from '../contexts/AuthContext';
import { FeatureService, FeatureKey } from '../services/featureService';
import { Subscription, SubscriptionAddon } from '../types/subscription';
import { addonService } from '../services/addonService';

function isPartnerClient(user: { insurer_org_id?: number; role?: string } | null | undefined): boolean {
  return Boolean(user?.insurer_org_id && user?.role === 'USER');
}

export const useSubscription = () => {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [addons, setAddons] = useState<SubscriptionAddon[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      if (isPartnerClient(user)) {
        setSubscription(null);
        setAddons([]);
        setError(null);
        setIsLoading(false);
        return;
      }
      checkSubscription();
    } else if (!isAuthenticated && !authLoading) {
      setSubscription(null);
      setAddons([]);
      setError(null);
      setIsLoading(false);
    }
  }, [isAuthenticated, authLoading, user]);

  const checkSubscription = async () => {
    if (isPartnerClient(user)) {
      setSubscription(null);
      setAddons([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const currentSub = await paymentService.getCurrentSubscription();
      setSubscription(currentSub);

      // Fetch addons if subscription exists
      if (currentSub?.id) {
        try {
          const subscriptionAddons = await addonService.getSubscriptionAddons(currentSub.id);
          setAddons(subscriptionAddons);
          // Also update subscription object with addons for backward compatibility
          if (currentSub) {
            currentSub.addons = subscriptionAddons;
          }
        } catch (addonError) {
          // If addons fetch fails, continue without them (backward compatible)
          console.warn('Failed to fetch addons:', addonError);
          setAddons([]);
        }
      } else {
        setAddons([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to check subscription');
    } finally {
      setIsLoading(false);
    }
  };

  const hasActiveSubscription = () => {
    return !!(subscription && subscription.status === 'active');
  };

  const canAccessFeature = (feature: FeatureKey) => {
    return FeatureService.canAccessFeature(subscription, feature, user, addons);
  };

  const hasAddon = (addonType: string): boolean => {
    return addons.some(addon => addon.addon_type === addonType && addon.status === 'active');
  };

  const refreshSubscription = () => {
    // Only refresh if user is authenticated
    if (isAuthenticated && !authLoading) {
      checkSubscription();
    }
  };

  return {
    subscription,
    addons,
    isLoading,
    error,
    hasActiveSubscription,
    canAccessFeature,
    hasAddon,
    refreshSubscription
  };
};
